import { useState } from 'react';
import { ConfigurationPortail } from './ConfigurationPortail';
import { SuggestionsSignalement } from './SuggestionsSignalement';
import { PiecesJointes } from './PiecesJointes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { REPORT_CATEGORIES, SOURCE_LABELS, type Domain } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';

interface Signalement {
  id: string;
  titre: string;
  description: string;
  reference: string;
  statut: 'NEW' | 'LINKED' | 'REJECTED';
  incident_id: string | null;
  version: number;
  localisation: string;
  motif: string | null;
  avis_note: number | null;
  avis_commentaire: string | null;
}
const libelles = { NEW: 'À qualifier', LINKED: 'Rattaché', REJECTED: 'Écarté' };
export function PageSignalementsReseau() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [domaine, setDomaine] = useState<Domain>('WATER');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const liste = useQuery({
    queryKey: ['signalements-reseau'],
    queryFn: () => requete<{ elements: Signalement[] }>('/signalements'),
    enabled: peut(contexte, 'signalement', 'voir'),
  });
  const provinces = useQuery({
    queryKey: ['provinces'],
    queryFn: () => requete<{ elements: { id: string; nom: string }[] }>('/referentiel/provinces'),
    enabled: creation,
  });
  const incidents = useQuery({
    queryKey: ['incidents-reseau', 'qualification'],
    queryFn: () => requete<{ elements: { id: string; titre: string }[] }>('/incidents'),
    enabled: peut(contexte, 'incident', 'voir'),
  });
  if (!peut(contexte, 'signalement', 'voir'))
    return <p role="alert">Accès aux signalements non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Signalements" />
      {peut(contexte, 'signalement', 'modifier') && <ConfigurationPortail />}
      {peut(contexte, 'signalement', 'creer') && (
        <button onClick={() => setCreation(!creation)}>
          {creation ? 'Fermer le formulaire' : 'Saisir un signalement'}
        </button>
      )}
      {creation && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 700, marginBlock: 20 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setEnCours(true);
            setErreur('');
            try {
              await requete('/signalements', {
                methode: 'POST',
                corps: {
                  domaine,
                  categorie: d.get('categorie'),
                  titre: d.get('titre'),
                  description: d.get('description'),
                  source: d.get('source'),
                  province_id: d.get('province_id'),
                  localisation: d.get('localisation'),
                  latitude: d.get('latitude') ? Number(d.get('latitude')) : null,
                  longitude: d.get('longitude') ? Number(d.get('longitude')) : null,
                },
              });
              setCreation(false);
              await client.invalidateQueries({ queryKey: ['signalements-reseau'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <label>
            Service{' '}
            <select value={domaine} onChange={(e) => setDomaine(e.target.value as Domain)}>
              <option value="WATER">Eau</option>
              <option value="ELECTRICITY">Électricité</option>
            </select>
          </label>
          <label>
            Catégorie{' '}
            <select name="categorie" key={domaine}>
              {REPORT_CATEGORIES[domaine].map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Titre <input name="titre" required minLength={5} maxLength={200} />
          </label>
          <label>
            Description <textarea name="description" required minLength={5} maxLength={2000} />
          </label>
          <label>
            Source{' '}
            <select name="source">
              {['AGENT', 'CALL_CENTER', 'PARTNER', 'CNEE'].map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Province{' '}
            <select name="province_id" required>
              <option value="">Choisir une province</option>
              {provinces.data?.elements.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Localisation <input name="localisation" required minLength={2} maxLength={250} />
          </label>
          <fieldset>
            <legend>Coordonnées connues (facultatif)</legend>
            <p>Renseigner les deux coordonnées ou laisser les deux champs vides.</p>
            <label>
              Latitude <input name="latitude" type="number" step="any" min={-4} max={2.5} />
            </label>
            <label>
              Longitude <input name="longitude" type="number" step="any" min={8} max={15} />
            </label>
          </fieldset>
          <button disabled={enCours || provinces.isPending}>Enregistrer le signalement</button>
          {provinces.error && <p role="alert">{messageErreur(provinces.error)}</p>}
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {liste.isPending && <p role="status">Chargement…</p>}
      {liste.error && <p role="alert">{messageErreur(liste.error)}</p>}
      {liste.data && (
        <p>{liste.data.elements.length} signalement(s), au maximum les 200 plus récents.</p>
      )}
      {liste.data?.elements.map((s) => (
        <article key={s.id} style={{ borderBottom: '1px solid currentColor', paddingBlock: 20 }}>
          <h2>{s.titre}</h2>
          <PiecesJointes
            type="signalement"
            id={s.id}
            ecriture={peut(contexte, 'signalement', 'modifier')}
          />
          <p>
            {s.reference} · {libelles[s.statut]} · {s.localisation}
          </p>
          <p>{s.description}</p>
          {s.motif && <p>Motif : {s.motif}</p>}
          {s.statut === 'NEW' &&
            peut(contexte, 'signalement', 'modifier') &&
            peut(contexte, 'incident', 'voir') && (
              <SuggestionsSignalement id={s.id} version={s.version} />
            )}
          {s.incident_id && peut(contexte, 'incident', 'voir') && (
            <Link to={`/incidents/${s.incident_id}`}>Consulter l’incident lié</Link>
          )}
          {s.statut !== 'REJECTED' && peut(contexte, 'signalement', 'modifier') && (
            <form
              style={{ display: 'grid', gap: 10, maxWidth: 700 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                setEnCours(true);
                setErreur('');
                try {
                  await requete(`/signalements/${s.id}/qualification`, {
                    methode: 'POST',
                    corps: {
                      action: d.get('action'),
                      motif: d.get('motif'),
                      version: s.version,
                      ...(d.get('incident_id') ? { incident_id: d.get('incident_id') } : {}),
                    },
                  });
                  await client.invalidateQueries({ queryKey: ['signalements-reseau'] });
                  await client.invalidateQueries({ queryKey: ['incidents-reseau'] });
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setEnCours(false);
                }
              }}
            >
              <label>
                Décision{' '}
                <select name="action">
                  {s.statut === 'LINKED' ? (
                    <option value="detacher">Dissocier le rattachement</option>
                  ) : (
                    <>
                      {peut(contexte, 'incident', 'creer') && (
                        <option value="creer_incident">
                          Créer un incident à partir du signalement
                        </option>
                      )}
                      {peut(contexte, 'incident', 'voir') && (
                        <option value="rattacher">Rattacher à un incident existant</option>
                      )}
                      <option value="rejeter">Écarter le signalement</option>
                    </>
                  )}
                </select>
              </label>
              {s.statut === 'NEW' && peut(contexte, 'incident', 'voir') && (
                <label>
                  Incident existant (pour rattachement){' '}
                  <select name="incident_id">
                    <option value="">Choisir un incident</option>
                    {incidents.data?.elements.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.titre}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Motif <input name="motif" minLength={3} maxLength={500} required />
              </label>
              <button disabled={enCours}>Appliquer la décision</button>
            </form>
          )}
          {s.avis_note !== null && (
            <section aria-label="Avis citoyen">
              <h3>Avis citoyen : {s.avis_note}/5</h3>
              <p style={{ whiteSpace: 'pre-wrap' }}>{s.avis_commentaire || 'Sans commentaire'}</p>
            </section>
          )}
        </article>
      ))}
    </section>
  );
}
